import { useEffect, useRef, useState } from "react";
import { FileText, ShieldCheck, Receipt } from "lucide-react";
import { SelectInput, YesNoInput, FileUpload, MultiFileUpload } from "../components/fields";
import { Alert } from "../components/Toast";
import { EmployeeListingSection } from "../components/EmployeeListingSection";
import { useFormStore } from "../store/formStore";
import { useUiStore } from "../store/uiStore";
import { useStep7Visibility } from "../hooks/useStep7Visibility";
import { DOC_VERIFICATION_SLOT } from "../hooks/step7Rules";
import { getApi } from "../api";
import { trackWarning } from "../telemetry/appInsights";
import type { useStepValidation } from "../hooks";

interface StepDocumentsProps {
  validation: ReturnType<typeof useStepValidation>;
}

const DOC_VERIFICATION_OPTIONS = [
  { value: "", label: "Select one..." },
  { value: "State Wage and Tax", label: "State Wage and Tax" },
  { value: "Payroll Records", label: "Payroll Records" },
  { value: "Business in operation less than 90 days", label: "Business in operation less than 90 days" },
];

const ORG_TYPE_DOCS: Record<string, string[]> = {
  "Sole Proprietorship (Farm & Non-Farm)": ["Schedule C or Schedule F (most recent filed; 1040)"],
  "Single Member LLC": ["Schedule C (most recent one filed that confirms single-member pass-through treatment)"],
  "PLLC / PC / PA": ["Schedule K-1 (for each owner seeking coverage)"],
  "Partnerships (LP/LLP)": ["Schedule K-1 for each owner"],
  "Multi-Member LLC (Partnership Treatment)": ["K-1 (for each owner seeking coverage)"],
  "S Corporation": ["Schedule K-1 (for each shareholder seeking coverage)"],
  "Grantor Trusts": [
    "Trust Agreement (or a Certification of Trust that summarizes key terms)",
    "Evidence of grantor's ownership of underlying business interest (e.g., K-1 or Sch. C in the grantor's name or SSN)",
  ],
};

export function StepDocuments({ validation }: StepDocumentsProps) {
  const {
    showRequiredDocuments,
    showPriorBill,
    showOwnerEligibility,
    showNewlyFormedToggle,
    showNewlyFormedDocs,
  } = useStep7Visibility();

  // Touch counter — same pattern as Step 3. Captures mount value so previous-step
  // toasts don't immediately show errors on this step's file uploads.
  const globalTouchCounter = useUiStore((s) => s.validationTouchCounter);
  const [mountCounter] = useState(globalTouchCounter);
  const touchAll = Math.max(0, globalTouchCounter - mountCounter);

  const docVerificationType = useFormStore((s) => s.data.docVerificationType);
  const orgType = useFormStore((s) => s.data.orgType);
  // Renewals: documents & census are optional (Mike Bentz, 2026-08-12) — required
  // markers/aria-required must not claim otherwise.
  const isRenewal = useFormStore((s) => s.meta.isRenewal);
  const documents = useFormStore((s) => s.data.documents);
  const ownerEligibilityDocs = useFormStore((s) => s.data.ownerEligibilityDocs);
  const setDocument = useFormStore((s) => s.setDocument);
  const addOwnerEligibilityDoc = useFormStore((s) => s.addOwnerEligibilityDoc);
  const removeOwnerEligibilityDoc = useFormStore((s) => s.removeOwnerEligibilityDoc);

  // Cross-step field cleanup (newlyFormedCompany + section-hide file cleanup) is
  // handled by the store-level subscription in formStore.ts so it runs regardless
  // of which step is mounted. The effect below handles the *within-section*
  // doc-type switch (e.g. State Wage → Payroll) — that case isn't covered by the
  // section-hide path because the section is still visible.
  const prevDocType = useRef(docVerificationType);
  useEffect(() => {
    const prev = prevDocType.current;
    prevDocType.current = docVerificationType;
    if (prev === docVerificationType) return;

    const api = getApi();
    const prevKey = prev ? DOC_VERIFICATION_SLOT[prev] : null;
    if (prevKey) {
      const doc = useFormStore.getState().data.documents[prevKey];
      if (doc) {
        void api.deleteFile(doc.fileId).catch(() => trackWarning("StepDocuments: cleanup delete failed", { fileId: doc.fileId }));
        setDocument(prevKey, null);
      }
    }
  }, [docVerificationType, setDocument]);

  const orgTypeDocs = orgType ? ORG_TYPE_DOCS[orgType] : null;

  return (
    <div>
      <h1 className="text-2xl font-bold text-brand-navy mb-1">Documents & Employees</h1>
      <p className="text-gray-500 mb-6">{isRenewal ? "Documents and census are optional for renewal updates" : "Upload required documents and add employees"}</p>

      {showRequiredDocuments && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 section-reveal">
          <h2 className="text-lg font-semibold text-brand-navy mb-4 flex items-center gap-2">
            <FileText size={20} className="text-gray-500" aria-hidden="true" />
            Required Documents
          </h2>

          <Alert
            type="info"
            message="If your business has been in operation for more than 90 days, we require the most recent State Quarterly Wage & Tax report OR 90 days of payroll records. Which would you like to provide?"
            className="mb-4"
          />

          <SelectInput
            field="docVerificationType"
            label="Employment verification document"
            validation={validation}
            options={DOC_VERIFICATION_OPTIONS}
            required={!isRenewal}
          />

          {docVerificationType === "State Wage and Tax" && (
            <div className="mt-4 section-reveal">
              <div className="bg-gray-100 rounded-lg p-4 text-sm text-gray-600 mb-3 border border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-2">State Quarterly Wage & Tax Requirements</h3>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>Most recent State Quarterly Wage and Tax Report</li>
                  <li>Federal Wage and Tax will <strong>not</strong> be accepted</li>
                  <li>If your employees reside in multiple states, there will be separate Wage and Tax reports for each state of employment — the latest report for each state is required</li>
                  <li>Please annotate for each employee: <strong>FTE</strong> = Full Time Enrolling, <strong>Term</strong> = Terminated (provide date), <strong>FTW</strong> = Full Time Waiving, <strong>PT</strong> = Part Time, or <strong>NE</strong> = Not Eligible (provide reason)</li>
                </ul>
              </div>
              <FileUpload
                docType="wageTax"
                label="State Quarterly Wage & Tax"
                currentFile={documents.wageTax}
                onUpload={(ref) => setDocument("wageTax", ref)}
                onDelete={() => setDocument("wageTax", null)}
                required={!isRenewal}
                error={validation.getFieldError("documents.wageTax")}
                touchAllCounter={touchAll}
              />
            </div>
          )}

          {docVerificationType === "Payroll Records" && (
            <div className="mt-4 section-reveal">
              <div className="bg-gray-100 rounded-lg p-4 text-sm text-gray-600 mb-3 border border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-2">Payroll Records Requirements</h3>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>Most recent <strong>90 days</strong> of payroll must be provided</li>
                  <li>Must include: Name of Company, Wages, Hours Worked, Employee Name, Federal Withholdings, State Withholdings (if applicable), and Prep Date</li>
                  <li>Please annotate for each employee: <strong>FTE</strong> = Full Time Enrolling, <strong>Term</strong> = Terminated (provide date), <strong>FTW</strong> = Full Time Waiving, <strong>PT</strong> = Part Time, or <strong>NE</strong> = Not Eligible (provide reason)</li>
                </ul>
              </div>
              <FileUpload
                docType="payroll"
                label="Payroll Records"
                currentFile={documents.payroll}
                onUpload={(ref) => setDocument("payroll", ref)}
                onDelete={() => setDocument("payroll", null)}
                required={!isRenewal}
                error={validation.getFieldError("documents.payroll")}
                touchAllCounter={touchAll}
              />
            </div>
          )}

          {docVerificationType === "Business in operation less than 90 days" && (
            <div className="mt-4 section-reveal">
              <div className="bg-gray-100 rounded-lg p-4 text-sm text-gray-600 mb-3 border border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-2">For Wage & Tax or Payroll Record provide one of the following:</h3>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>Payroll records for a minimum of <strong>30 days</strong> (preferred)</li>
                  <li>Completed W-4 forms for employees</li>
                </ul>
                <h3 className="font-semibold text-gray-800 mt-4 mb-2">Provide one of the documents below validating newly formed business start date:</h3>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>Articles of Organization for LLCs (AKA: certificate of formation, certificate of organization), or Articles of Incorporation for Corporations (AKA: certificate of incorporation). May be found on Secretary of State Filings.</li>
                  <li>Business license / permits showing the start date</li>
                  <li>Proof of physical business operations starting on a given date (lease or deed, utility bills in the business name)</li>
                  <li>Effective date of the first business bank account or merchant account</li>
                  <li>First tax return filing date for the entity (or the owner's personal return if applicable)</li>
                  <li>Sec. 8(a)/SBA or other government program eligibility start date documents (if applicable)</li>
                </ul>
              </div>
              <FileUpload
                docType="newBusinessDocs"
                label="Required Documents for newly formed business"
                currentFile={documents.newBusinessDocs}
                onUpload={(ref) => setDocument("newBusinessDocs", ref)}
                onDelete={() => setDocument("newBusinessDocs", null)}
                required={!isRenewal}
                error={validation.getFieldError("documents.newBusinessDocs")}
                touchAllCounter={touchAll}
              />
            </div>
          )}

        </div>
      )}

      {showPriorBill && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 section-reveal">
          <h2 className="text-lg font-semibold text-brand-navy mb-4 flex items-center gap-2">
            <Receipt size={20} className="text-gray-500" aria-hidden="true" />
            Prior Carrier Bill
          </h2>
          <FileUpload
            docType="priorBill"
            label="Prior Carrier Bill"
            currentFile={documents.priorBill}
            onUpload={(ref) => setDocument("priorBill", ref)}
            onDelete={() => setDocument("priorBill", null)}
            hint="Please annotate which employees are Enrolling, Terminated, Cobra, No Longer Eligible (provide reason), or Waiving."
            hintPosition="above"
            required={!isRenewal}
            error={validation.getFieldError("documents.priorBill")}
            touchAllCounter={touchAll}
          />
        </div>
      )}

      {showOwnerEligibility && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 section-reveal">
          <h2 className="text-lg font-semibold text-brand-navy mb-4 flex items-center gap-2">
            <ShieldCheck size={20} className="text-gray-500" aria-hidden="true" />
            Owner Eligibility Verification
          </h2>

          {showNewlyFormedToggle && (
            <div className="mb-4">
              <YesNoInput
                field="newlyFormedCompany"
                label="Was this company established within the last 90 days?"
                validation={validation}
                required={!isRenewal}
              />
            </div>
          )}

          <Alert
            type="info"
            message={<>Owner eligibility verification is required for your organization type of <strong className={orgType ? "text-info" : "text-danger"}>{orgType || "organization type not selected"}</strong>. <strong>Please upload the below document(s).</strong></>}
            className="mb-4"
          />

          {showNewlyFormedDocs ? (
            <div className="bg-gray-100 rounded-lg p-4 text-sm text-gray-600 mb-4 border border-gray-200">
              <h3 className="font-semibold text-gray-800 mb-2">Provide one of the documents below:</h3>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Articles of Organization for LLCs / Articles of Incorporation for Corporations (Secretary of State Filings)</li>
                <li>Business license / permits showing the start date</li>
                <li>Proof of physical business operations starting on a given date (lease or deed, utility bills in the business name)</li>
                <li>Effective date of the first business bank account or merchant account</li>
                <li>First tax return filing date for the entity (or the owner's personal return if applicable)</li>
                <li>Sec. 8(a)/SBA or other government program eligibility start date documents (if applicable)</li>
              </ul>
            </div>
          ) : orgTypeDocs ? (
            <div className="bg-gray-100 rounded-lg p-4 text-sm text-gray-600 mb-4 border border-gray-200">
              <ul className="list-disc pl-5 space-y-1.5">
                {orgTypeDocs.map((doc, i) => <li key={i}>{doc}</li>)}
              </ul>
            </div>
          ) : orgType ? (
            <div className="bg-gray-100 rounded-lg p-4 text-sm text-gray-600 mb-4 border border-gray-200">
              Document requirements pending — please contact your underwriter.
            </div>
          ) : (
            <div className="bg-gray-100 rounded-lg p-4 text-sm text-gray-600 mb-4 border border-gray-200">
              Specify your organization type on the Company page of this application to see required document(s).
            </div>
          )}

          <MultiFileUpload
            docType="ownerEligibilityDocs"
            label="Owner Eligibility Documents"
            hint="Upload all required documents for owner verification"
            files={ownerEligibilityDocs}
            onAdd={addOwnerEligibilityDoc}
            onRemove={removeOwnerEligibilityDoc}
          />

          <p className="text-xs text-gray-500 mt-2 italic">
            This upload is not required but is recommended to avoid delays in processing your application.
          </p>
        </div>
      )}

      <EmployeeListingSection validation={validation} />
    </div>
  );
}
